import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTL, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, Edit, FileDown, ArrowRight, X, MessageSquare, CheckCircle2, XCircle, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

export default function Quotes() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [company, setCompany] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (company !== "all") params.company_id = company;
    const { data } = await api.get("/quotes", { params });
    setQuotes(data);
    setPage(1);
  };
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    load();
  }, []);
  useEffect(() => { load(); }, [status, company]);

  const filtered = q
    ? quotes.filter((x) => (x.quote_no || "").toLowerCase().includes(q.toLowerCase()) ||
                            (x.company_name || "").toLowerCase().includes(q.toLowerCase()))
    : quotes;
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const downloadPdf = (qid, qno) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${qid}/pdf?auth=${token()}`;
    window.open(url, "_blank");
  };

  const copyPublicLink = (publicToken) => {
    if (!publicToken) {
      toast.error("Bu teklif için müşteri onay linki bulunmuyor");
      return;
    }
    const link = `${window.location.origin}/q/${publicToken}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Müşteri onay linki kopyalandı"),
      () => toast.error("Kopyalanamadı")
    );
  };

  const convertToOrder = async (qid) => {
    if (!window.confirm("Teklifi siparişe dönüştürmek istediğinize emin misiniz? Stoktan otomatik düşüş olacaktır.")) return;
    try {
      const { data } = await api.post(`/quotes/${qid}/convert`);
      toast.success(`Sipariş oluşturuldu: ${data.order_no}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dönüştürme başarısız");
    }
  };

  const changeStatus = async (qid, newStatus) => {
    try {
      await api.patch(`/quotes/${qid}/status`, null, { params: { status: newStatus } });
      load();
    } catch {}
  };

  const remove = async (qid, qno) => {
    if (!window.confirm(`${qno} numaralı teklifi kalıcı olarak silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/quotes/${qid}`);
      toast.success("Teklif silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 lg:p-5">
        <div className="grid lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Ara</label>
            <Input data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Teklif no, firma..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Firma</label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Durum</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="draft">Taslak</SelectItem>
                <SelectItem value="sent">Gönderildi</SelectItem>
                <SelectItem value="approved">Onaylandı</SelectItem>
                <SelectItem value="rejected">Onaylanmadı</SelectItem>
                <SelectItem value="converted">Siparişe Dönüştü</SelectItem>
                <SelectItem value="revision_requested">Revizyon İstendi</SelectItem>
                <SelectItem value="expired">Süresi Doldu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="text-[#08285A] font-bold">{filtered.length}</span> teklif
        </div>
        <div className="flex gap-2">
          <ExportButton type="quotes" />
          <Button onClick={() => navigate("/quotes/new")} data-testid="new-quote-btn"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> Yeni Teklif
          </Button>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Teklif No</th>
                <th className="text-left px-4 py-3 font-semibold">Firma</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Geçerlilik</th>
                <th className="text-left px-4 py-3 font-semibold">Hazırlayan</th>
                <th className="text-right px-4 py-3 font-semibold">Tutar</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]">Teklif bulunamadı</td></tr>
              ) : paged.map((qt) => (
                <tr key={qt.id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                    data-testid={`quote-row-${qt.id}`}>
                  <td className="px-4 py-3 font-bold text-[#0B3676] font-mono text-xs">{qt.quote_no}</td>
                  <td className="px-4 py-3 font-semibold text-[#111827]">{qt.company_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(qt.quote_date)}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(qt.valid_until)}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{qt.prepared_by_name}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#08285A]">{fmtTL(qt.total_amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={qt.status} />
                    {qt.customer_decision === "approved" && (
                      <div className="flex items-center gap-1 text-[10px] text-[#16A34A] font-bold mt-1 justify-center">
                        <CheckCircle2 size={11} /> Müşteri Onayladı
                      </div>
                    )}
                    {qt.customer_decision === "rejected" && (
                      <div className="flex items-center gap-1 text-[10px] text-[#DC2626] font-bold mt-1 justify-center">
                        <XCircle size={11} /> Müşteri Reddetti
                      </div>
                    )}
                    {qt.customer_decision === "revision_requested" && (
                      <div className="flex items-center gap-1 text-[10px] text-[#F59E0B] font-bold mt-1 justify-center"
                           title={qt.customer_revision_note}>
                        <MessageSquare size={11} /> Revize İstendi
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => copyPublicLink(qt.public_token)}
                            data-testid={`copy-link-${qt.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded"
                            title="Müşteri Onay Linkini Kopyala"><Copy size={14} /></button>
                    <button onClick={() => downloadPdf(qt.id, qt.quote_no)}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            data-testid={`pdf-quote-${qt.id}`}
                            title="PDF İndir"><FileDown size={14} /></button>
                    <button onClick={() => navigate(`/quotes/${qt.id}/edit`)}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded ml-1"
                            title="Düzenle"><Edit size={14} /></button>
                    {qt.status !== "converted" && qt.company_id && (
                      <button onClick={() => convertToOrder(qt.id)}
                              data-testid={`convert-quote-${qt.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#16A34A] hover:bg-[#16A34A]/10 rounded ml-1"
                              title="Siparişe Dönüştür"><ArrowRight size={14} /></button>
                    )}
                    {qt.status === "draft" && (
                      <button onClick={() => changeStatus(qt.id, "sent")}
                              className="text-[10px] text-[#0B3676] font-bold ml-2 hover:underline">
                        Gönder
                      </button>
                    )}
                    <button onClick={() => remove(qt.id, qt.quote_no)}
                            data-testid={`delete-quote-${qt.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            title="Sil"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
            <div className="text-[#6B7280]">Sayfa {page} / {totalPages}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Önceki</Button>
              <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Sonraki</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
